import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Form } from "react-bootstrap";
import { AddUsersWrapper } from "./styled";
import {
  BackButton,
  Box,
  ButtonStyled,
  TabsList,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import { userFormTabs } from "../../../helpers/adminConstants/AdminConstants";
import { ContentWrapperWithoutYMargin } from "../../../components/styled/styled";
import {
  LeftArrowBlack,
  RightArrowWhite,
  RightArrowWithoutBar,
} from "../../../components/styled/AllImages";
import UsersApi from "../../../services/api/usersApi";
import { toast } from "react-toastify";

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = true;
  const { t } = useTranslation();
  const {
    register,
    control,
    handleSubmit,
    trigger,
    watch,
    resetField,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const permissionsObj = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
  };

  const getEditUserInfo = async () => {
    setLoading(true);
    try {
      const { data, code } = await UsersApi.getEditUserInfo(id);

      if (code === 200) {
        reset({
          ...data,
          job_title: data?.user_detail?.job_title ?? "",
          id_number: data?.user_detail?.id_number ?? "",
          additional_information:
            data?.user_detail?.additional_information ?? "",
        });

        const mapped = {};
        data.user_permissions?.forEach(
          ({ permission_type_id, permission_operation_id }) => {
            const typeId = parseInt(permission_type_id);
            const opId = parseInt(permission_operation_id);

            if (!mapped[typeId]) {
              mapped[typeId] = [];
            }

            if (!mapped[typeId].includes(opId)) {
              mapped[typeId].push(opId);
            }

            const hasAllFour = [1, 2, 3, 4].every((id) =>
              mapped[typeId].includes(id)
            );
            if (hasAllFour && !mapped[typeId].includes("all")) {
              mapped[typeId].unshift("all");
            }
          }
        );

        setPermissions(mapped);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState(userFormTabs[0]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState(permissionsObj);
  const currentTabIndex = userFormTabs.findIndex(
    (tab) => tab.id === activeTab.id
  );
  const handleTabChange = async (tabIndex) => {
    const isValid = await trigger(activeTab.fields);
    if (isValid) {
      setActiveTab(userFormTabs[tabIndex]);
    }
  };

  const handleNext = async () => {
    const isValid = await trigger(activeTab.fields);
    if (isValid) setActiveTab(userFormTabs[currentTabIndex + 1]);
  };

  const onSubmit = async (data) => {
    const permissionsObj = Object.fromEntries(
      Object.entries(permissions).map(([key, value]) => [
        key,
        value.filter((perm) => perm !== "all"),
      ])
    );

    const hasSelectedPermissions = Object.values(permissionsObj).some(
      (perms) => perms.length > 0
    );

    if (!hasSelectedPermissions) {
      toast.error(t("please_select_permission"));
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      const excludedKeys = [
        "user_permissions",
        "user_detail",
        "created_at",
        "updated_at",
        "created_by",
        "onboarding_shown",
        "organization_id",
        "language_type",
        "tap_customer_id",
        "profile",
        "active",
        "id",
      ];

      const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
        if (!excludedKeys.includes(key)) {
          acc[key] = value;
        }
        return acc;
      }, {});

      for (const key in filteredData) {
        formData.append(key, filteredData[key]);
      }

      formData.append("action", "update");
      formData.append("user_id", id);
      formData.append("permissions", JSON.stringify(permissionsObj));

      const { code, message } = await UsersApi.createUser(formData);

      if (code === 200) {
        toast.success(t("user_updated_successfully"));
        navigate("/users");
      } else if (code === 204) {
        toast.error(message);
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEditUserInfo();
  }, []);

  return (
    <AddUsersWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("edit_user")}
            onClick={() => navigate("/users")}
          />
          <Breadcrumbs />
        </Box>
      </Box>

      <Box className="tabs-header-wrapper">
        <TabsList
          tabs={userFormTabs}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
        />
      </Box>
      <ContentWrapperWithoutYMargin>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <activeTab.component
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            resetField={resetField}
            setValue={setValue}
            permissions={permissions}
            setPermissions={setPermissions}
            isEdit={isEdit}
          />
          <Box className="d-flex flex-column-reverse flex-sm-row justify-content-between mx-3 py-4 gap-3">
            <ButtonStyled
              icon={RightArrowWhite}
              label={t("back")}
              border="var(--borderExtraLight)"
              color="var(--text)"
              bg="transparent"
              type="button"
              onClick={() => setActiveTab(userFormTabs[0])}
              disabled={activeTab === userFormTabs[0]}
            />
            {activeTab === userFormTabs[0] && (
              <Box className="d-flex align-items-center gap-3">
                <ButtonStyled
                  border="var(--borderExtraLight)"
                  iconBefore={LeftArrowBlack}
                  bg="transparent"
                  label={t("Cancel")}
                  color="var(--text)"
                  type="button"
                />
                <ButtonStyled
                  label={t("next")}
                  icon={RightArrowWhite}
                  type="button"
                  bg="var(--themeColor)"
                  onClick={handleNext}
                />
              </Box>
            )}
            {activeTab === userFormTabs[1] && (
              <Box className="d-flex align-items-center gap-3">
                <ButtonStyled
                  border="var(--borderExtraLight)"
                  iconBefore={LeftArrowBlack}
                  bg="transparent"
                  label={t("Cancel")}
                  color="var(--text)"
                  type="button"
                />
                <ButtonStyled
                  icon={RightArrowWhite}
                  type="submit"
                  label={t("update")}
                  isLoading={loading}
                  bg="var(--themeColor)"
                />
              </Box>
            )}
          </Box>
        </Form>
      </ContentWrapperWithoutYMargin>
    </AddUsersWrapper>
  );
};

export default EditUser;
